import React from "react";
import { motion } from "framer-motion";
import { Container, Section, Eyebrow, DisplayHeading, BodyCopy, Reveal } from "@/components/kit";
import { fadeUp, stagger, viewport } from "@/lib/motion";

const QUESTIONS = [
  { q: "Is it safe?", a: "Can your AI be manipulated, jailbroken, or made to leak data?" },
  { q: "Is it fair?", a: "When it screens a person, can you show it treats them equally?" },
  { q: "Can you prove it?", a: "When an auditor asks for evidence, do you have anything to show?" },
];

const CLAIMS = ["“Safe”", "“Governed”", "“Compliant”"];
const EVIDENCE = ["Testing", "Controls", "Findings", "Audit trail", "Evidence"];

export default function Problem() {
  return (
    <Section id="problem" className="border-t border-line">
      <Container>
        <Reveal>
          <Eyebrow>The trust gap</Eyebrow>
        </Reveal>
        <DisplayHeading className="mt-6 max-w-3xl text-3xl leading-[1.08] sm:text-4xl md:text-5xl">
          AI is moving faster <span className="text-content-faint">than trust.</span>
        </DisplayHeading>
        <Reveal i={1}>
          <BodyCopy className="mt-6 max-w-2xl">
            The question is no longer “Are we using AI?” — it’s whether your AI is safe, accountable,
            and provable. Three questions decide it.
          </BodyCopy>
        </Reveal>

        {/* editorial questions — no cards, typographic rhythm */}
        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewport} className="mt-10 flex flex-col divide-y divide-line-soft border-y border-line-soft sm:mt-16">
          {QUESTIONS.map((item, i) => (
            <motion.div key={item.q} variants={fadeUp} className="group grid items-baseline gap-3 py-6 sm:gap-4 sm:py-8 md:grid-cols-[auto_1fr_1.2fr] md:gap-10">
              <span className="font-mono text-xs text-content-faint">0{i + 1}</span>
              <h3 className="font-display text-[28px] font-medium leading-none text-content transition-colors group-hover:text-brand-soft sm:text-4xl md:text-5xl">
                {item.q}
              </h3>
              <p className="max-w-md text-[15px] leading-relaxed text-content-muted">{item.a}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* transformation: claims -> meira -> proof */}
        <Reveal i={1}>
          <div className="mt-12 sm:mt-16">
            <div className="mb-8 flex items-center gap-4">
              <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-content-faint">Claims aren’t proof</span>
              <span className="h-px flex-1 bg-panel-raised" />
            </div>
            <div className="grid items-center gap-6 md:grid-cols-[1fr_auto_1.3fr_auto_1fr]">
              {/* claims */}
              <div className="flex flex-col gap-2">
                {CLAIMS.map((c) => (
                  <span key={c} className="rounded-full border border-dashed border-line-bright px-4 py-2 text-center text-sm text-content-muted">{c}</span>
                ))}
              </div>
              <Arrow />
              {/* meira turns them into evidence */}
              <div className="relative rounded-lg border border-brand/25 bg-gradient-to-b from-brand/8 to-transparent p-5">
                <div className="mb-3 text-center font-display text-sm tracking-wide text-brand-soft">MEIRA</div>
                <div className="flex flex-wrap justify-center gap-2">
                  {EVIDENCE.map((e) => (
                    <span key={e} className="rounded-full border border-line bg-panel px-3 py-1 font-mono text-[11px] tracking-wide text-content">{e}</span>
                  ))}
                </div>
              </div>
              <Arrow />
              {/* proof */}
              <div className="flex flex-col items-center gap-2 rounded-lg border border-valid/25 bg-valid/5 p-5 text-center">
                <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-valid">Proof</span>
                <span className="font-display text-2xl font-medium text-valid valid-glow">Verified</span>
              </div>
            </div>
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}

function Arrow() {
  return (
    <div className="flex items-center justify-center py-1 md:py-0">
      <span className="text-content-faint md:rotate-0">
        <svg width="34" height="12" viewBox="0 0 34 12" fill="none" className="rotate-90 md:rotate-0">
          <path d="M0 6h30m0 0l-5-5m5 5l-5 5" stroke="currentColor" strokeWidth="1" />
        </svg>
      </span>
    </div>
  );
}
